import {bindable, customElement, inject} from "aurelia-framework";
import {EventAggregator} from "aurelia-event-aggregator";
import * as _ from "lodash";
import {Client} from "../api/client";
import {SubmitHandler} from "../form/submit/submit-handler";
import {FlashService} from "../flash/flash-service";
import {BindingSignaler} from "aurelia-templating-resources";
import {TaskQueue} from "aurelia-framework";
import Sortable from "sortablejs";
import {TranslatableService} from "../cell/type/translatable-service";
import {UnsavedChangesChecker} from "../form/unsaved-changes-checker";
import {UnsavedChanges, UnsavedChangesPrevent} from "../form/unsaved-changes-prevent";
import "./faq-editor.css";

@customElement('faq-editor')
@inject(Client, SubmitHandler, EventAggregator, FlashService, BindingSignaler, TaskQueue, TranslatableService, UnsavedChangesChecker)
export class FaqEditor {

    @bindable itinerary;
    @bindable editMode = false;

    client;
    submit;
    ea;
    flash;
    signaler;
    taskQueue;
    translatableService;
    unsavedChangesChecker;

    categories = [];
    selected = [];
    sortables = new Map();
    searchQuery = '';
    searchTimeout = null;
    limit = 100;
    offset = 0;
    paginationData = { items: [], filteredCount: 0 };
    skipUnsavedChangesCheck = false;
    originalUnsavedChanges = null;
    filteredCount = 0;
    totalFaqCount = 0;
    constructor(client, submit, ea, flash, signaler, taskQueue, translatableService, unsavedChangesChecker) {
        this.client = client;
        this.submit = submit;
        this.ea = ea;
        this.flash = flash;
        this.signaler = signaler;
        this.taskQueue = taskQueue;
        this.translatableService = translatableService;
        this.unsavedChangesChecker = unsavedChangesChecker;

        this.subscription = this.ea.subscribe('model-updated', payload => {
            if (payload?.modelId === 'tourism-faq/faq-question') {
                this.refreshAfterEdit();
            }
        });
    }

    translate(value) {
        if (!value) return '';
        return this.translatableService.getTranslation(value) || '';
    }

    stripHtml(html) {
        if (!html) return '';
        let tmp = document.createElement("div");
        tmp.innerHTML = html;
        return tmp.textContent || tmp.innerText || '';
    }

    truncate(text, length = 150) {
        if (!text) return '';
        return text.length > length ? text.substring(0, length) + '…' : text;
    }

    isSelected(category, question) {
        if (!category.selected) return false;
        return category.selected.some(s => String(s.id) === String(question.id));
    }

    _getControlUID(id) {
        return 'faq-editor-input-' + id;
    }

    canDeactivate() {
        if (this.skipUnsavedChangesCheck) {
            this.unsavedChangesChecker.revertChanges(this.originalUnsavedChanges);
            return true;
        }
        return this.unsavedChangesChecker.canDeactivate(this.originalUnsavedChanges);
    }

    refreshAfterEdit() {
        const currentSelection = [...this.selected];

        this.loadQuestions(this.searchQuery).then(() => {
            this.selected = [];
            this.categories.forEach(c => c.selected = []);

            currentSelection.forEach(category => {
                category.selected.forEach(q => this.addQuestion(q));
            });

            this.signaler.signal('faq-questions');
            this.taskQueue.queueMicroTask(() => this.initializeSorting());
        });
    }

    attached() {
        this.originalUnsavedChanges = _.clone(UnsavedChanges.changes);
        this.selected = [];
        this.categories = [];

        this.loadQuestions().then(() => {
            const faqRefs = this.itinerary.faq || [];

            const promises = faqRefs.map(ref => {
                const qid = ref && ref.id ? ref.id : ref;
                const question = this.findQuestionInCategories(qid);
                if (question) {
                    this.addQuestion(question);
                    return Promise.resolve();
                }
                return this.client.get(`tourism-faq/faq-question/${qid}`).then(response => {
                    const question = response && (response.item || response.items?.[0] || response);
                    if (question) this.addQuestion(question);
                });
            });

            Promise.all(promises).then(() => {
                this.originalFaq = this.selected.flatMap(cat => cat.selected.map(q => ({...q})));
                this.signaler.signal('faq-questions');
                this.taskQueue.queueMicroTask(() => this.initializeSorting());
            });
        });
    }

    findQuestionInCategories(id) {
        for (const category of this.categories) {
            const question = (category.questions || []).find(
                question => String(question.id) === String(id)
            );

            if (question) {
                return question;
            }
        }

        return null;
    }

    loadQuestions(search = '') {
        const conditions = {};
        if (search) {
            conditions.question = { $match: search };
        }
        const params = new URLSearchParams({
            limit: this.limit,
            offset: this.offset,
            conditions: JSON.stringify(conditions),
            loadCount: true,
        });
        params.append('embeds[]', 'category');

        return this.client.get(`tourism-faq/faq-question?${params}`)
            .then(response => {
                const questions = response.items || [];
                const groupedCategories = new Map();

                this.paginationData = response;
                this.filteredCount = response.filteredCount || 0;
                if (!search) {
                    this.totalFaqCount = this.filteredCount;
                }

                questions.forEach(question => {
                    const categoryId = String(question.category?.id);

                    if (!groupedCategories.has(categoryId)) {
                        groupedCategories.set(categoryId, {
                            id: categoryId,
                            title: question.category?.title,
                            questions: [],
                            selected: [],
                        });
                    }

                    groupedCategories.get(categoryId).questions.push(question);
                });

                Array.from(groupedCategories.values()).forEach(category => {
                    const existingCategory = this.categories.find(
                        existingCategory => String(existingCategory.id) === String(category.id)
                    );
                    category.selected = existingCategory ? existingCategory.selected : [];
                });
                this.categories = Array.from(groupedCategories.values());
                this.signaler.signal('faq-questions');
            });
    }

    onSearchInput() {
        clearTimeout(this.searchTimeout);
        this.searchTimeout = setTimeout(() => {
            this.offset = 0;
            this.loadQuestions(this.searchQuery);
        }, 300);
    }

    changeOffset(offset) {
        this.offset = offset;
        this.loadQuestions(this.searchQuery);
    }
    initializeSorting() {
        this.sortables.forEach((sortable) => sortable.destroy());
        this.sortables.clear();

        this.categories.forEach(category => {
            let element = document.getElementById(`sortable-table-${category.id}`);
            if (!element) return;

            let sortable = Sortable.create(element, {
                handle: '.fa-bars',
                animation: 150,
                onEnd: (event) => {
                    let cat = this.categories.find(c => c.id === event.from.id.replace('sortable-table-', ''));
                    if (cat) {
                        this.move(cat.selected, event.oldIndex, event.newIndex);
                    }
                }
            });

            this.sortables.set(category.id, sortable);
        });
    }

    detached() {
        this.selected = [];
        this.categories = [];
        this.sortables.forEach(sortable => sortable.destroy());
        this.sortables.clear();
        if (this.subscription) {
            this.subscription.dispose();
        }
    }

    search(list, id) {
        let result = undefined;
        if (list.length > 0) {
            _.forEach(list, item => {
                if (String(item.id) === String(id)) {
                    result = item;
                    return false;
                }
            });
        }
        return result;
    }

    addQuestion(question) {
        const category = this.categories.find(
            existingCategory => String(existingCategory.id) === String(question.category?.id)
        );
        if (!category) return;
        if (!category.selected) category.selected = [];
        if (this.search(category.selected, question.id)) return;
        if (!this.search(this.selected, category.id)) {
            this.selected.push(category);
        }
        category.selected.push(question);
        this.signaler.signal('faq-questions');
        this.updateItinerary();
    }

    removeQuestion(list, index, parentIndex) {
        list.splice(index, 1);

        if (0 === list.length) {
            this.selected.splice(parentIndex, 1);
        }

        this.signaler.signal('faq-questions');
        this.updateItinerary();
        this.taskQueue.queueMicroTask(() => this.initializeSorting());
    }

    updateItinerary() {
        UnsavedChangesPrevent.registerUnsavedChanges(this);
        this.itinerary.faq = [];
        _.forEach(this.selected, category => {
            _.forEach(category.selected, question => {
                this.itinerary.faq.push(question);
            });
        });
        this.signaler.signal('faq-questions');
    }

    save() {
        let faq = [];
        _.forEach(this.selected, category => {
            _.forEach(category.selected, question => {
                faq.push({modelId: question.modelId, id: question.id});
            });
        });

        this.submit.apiSubmit({
            config: {moduleId: 'tourism-faq', id: 'itinerary-faq'},
            identifier: this.itinerary.id,
            object: {faq: faq}
        }).then(
            () => {
                this.flash.success('form.success');
                this.skipUnsavedChangesCheck = true;
                UnsavedChangesPrevent.unregisterUnsavedChanges(this);
                this.editMode = false;
            },
            error => {
                let data = error.data;
                if (data && data.localizedMessage) {
                    this.flash.error(data.localizedMessage);
                } else if (400 !== error.status) {
                    this.flash.error('form.communication_error_message');
                } else {
                    this.flash.error('form.validation_error_message');
                }
            }
        );
    }

    move(list, oldIndex, newIndex) {
        let item = list[oldIndex];
        list.splice(oldIndex, 1);
        list.splice(newIndex, 0, item);
        this.updateItinerary();
        this.signaler.signal('faq-questions');
        this.taskQueue.queueMicroTask(() => this.initializeSorting());
    }

    originalFaq = [];

    toggleEditMode() {
        this.editMode = !this.editMode;
        if (this.editMode) {
            this.originalUnsavedChanges = _.clone(UnsavedChanges.changes);
            this.originalFaq = this.selected.flatMap(cat => cat.selected.map(q => ({...q})));
            this.loadQuestions().then(() => {
                this.selected = [];
                this.categories.forEach(c => c.selected = []);
                this.originalFaq.forEach(q => this.addQuestion(q));
                setTimeout(() => this.initializeSorting(), 100);
            });
        } else {
            UnsavedChangesPrevent.unregisterUnsavedChanges(this);
            this.searchQuery = '';
            this.selected = [];
            this.categories.forEach(c => c.selected = []);
            this.loadQuestions().then(() => {
                this.originalFaq.forEach(q => this.addQuestion(q));
                this.signaler.signal('faq-questions');
            });
        }
    }
}
